import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createFrontendPlugin,
  FrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  useApi,
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EmptyState,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LaunchIcon from '@material-ui/icons/Launch';
import RetryIcon from '@material-ui/icons/Replay';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import { useIsExternalUser } from '../externalUser';
import {
  DATADOG_GRAPHS_ANNOTATION,
  DATADOG_UI_URL,
  GraphDef,
  discoverConventionGraphs,
  getSavedGraphs,
  parseAnnotationGraphs,
  resetGraphs,
  saveGraphs,
  saveHealth,
} from './api';
import { GraphCard } from './GraphCard';
import { GraphDialog } from './GraphDialog';
import { HealthStrip, ServiceHealthCard, useHealthChecks } from './Health';
import { HealthDialog } from './HealthDialog';

const RANGES = [
  { label: '1h', seconds: 3600 },
  { label: '4h', seconds: 4 * 3600 },
  { label: '24h', seconds: 24 * 3600 },
  { label: '7d', seconds: 7 * 24 * 3600 },
];

// Where the currently shown graphs came from. 'saved' is the shared selection
// stored by the datadog-graphs backend; the others are fallback defaults.
type GraphSource = 'saved' | 'annotation' | 'discovered';

const sourceHint: Record<GraphSource, string> = {
  saved: 'Shared graphs - changes here apply for everyone.',
  annotation:
    "Defaults from the service's catalog-info.yaml - editing saves a shared copy.",
  discovered:
    'Auto-discovered from savr.* custom metrics - editing saves a shared copy.',
};

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: 18,
    cursor: 'default',
  },
  sectionTabs: {
    minHeight: 36,
    marginBottom: theme.spacing(2),
    '& .MuiTab-root': {
      minHeight: 36,
      textTransform: 'none',
      fontSize: 13,
      minWidth: 0,
    },
  },
  spacer: {
    flexGrow: 1,
  },
  rangePill: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: '3px 12px',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
    cursor: 'pointer',
    background: 'transparent',
    color: theme.palette.text.secondary,
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
  },
  rangePillActive: {
    color: theme.palette.type === 'dark' ? '#8F93FF' : '#4E4CCD',
    borderColor: theme.palette.type === 'dark' ? '#8F93FF' : '#4E4CCD',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(143, 147, 255, 0.12)'
        : 'rgba(78, 76, 205, 0.08)',
  },
}));

function DatadogContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const alertApi = useApi(alertApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const apis = useMemo(
    () => ({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error>();
  const [graphs, setGraphs] = useState<GraphDef[]>([]);
  const [source, setSource] = useState<GraphSource>('discovered');

  const [rangeSeconds, setRangeSeconds] = useState(RANGES[1].seconds);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedSection, setSelectedSection] = useState<string>();
  // Dialog state: closed | adding | editing graph at index.
  const [dialog, setDialog] = useState<{ open: boolean; editIndex?: number }>({
    open: false,
  });
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthReload, setHealthReload] = useState(0);
  const health = useHealthChecks(healthReload);
  // Editing controls only appear once the user is confirmed internal; the
  // backend enforces this regardless (permission-gated writes).
  const readonly = useIsExternalUser() !== false;

  const annotation = entity.metadata.annotations?.[DATADOG_GRAPHS_ANNOTATION];
  const { graphs: annotationGraphs, parseError } = useMemo(
    () => parseAnnotationGraphs(annotation),
    [annotation],
  );

  const loadDefaults = useCallback(async (): Promise<
    [GraphDef[], GraphSource]
  > => {
    if (annotationGraphs) {
      return [annotationGraphs, 'annotation'];
    }
    return [await discoverConventionGraphs(apis, entity), 'discovered'];
  }, [annotationGraphs, apis, entity]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(undefined);
    (async () => {
      const saved = await getSavedGraphs(apis, entity);
      if (saved.graphs) {
        return [saved.graphs, 'saved'] as [GraphDef[], GraphSource];
      }
      return await loadDefaults();
    })()
      .then(([result, resultSource]) => {
        if (!cancelled) {
          setGraphs(result);
          setSource(resultSource);
          setLoading(false);
        }
      })
      .catch(e => {
        if (!cancelled) {
          setLoadError(e);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apis, entity, loadDefaults]);

  // Every mutation persists the full list as the shared selection.
  const persist = useCallback(
    async (next: GraphDef[]) => {
      const previous = { graphs, source };
      setGraphs(next);
      setSource('saved');
      try {
        await saveGraphs(apis, entity, next);
      } catch (e) {
        setGraphs(previous.graphs);
        setSource(previous.source);
        alertApi.post({
          message: `Could not save graphs: ${e}`,
          severity: 'error',
        });
      }
    },
    [apis, entity, graphs, source, alertApi],
  );

  const handleReset = useCallback(async () => {
    try {
      await resetGraphs(apis, entity);
      const [defaults, defaultsSource] = await loadDefaults();
      setGraphs(defaults);
      setSource(defaultsSource);
    } catch (e) {
      alertApi.post({
        message: `Could not reset graphs: ${e}`,
        severity: 'error',
      });
    }
  }, [apis, entity, loadDefaults, alertApi]);

  if (loading) {
    return <Progress />;
  }
  if (loadError) {
    return <ResponseErrorPanel error={loadError} />;
  }
  if (parseError && source !== 'saved' && graphs.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="Invalid Datadog graph configuration"
        description={
          `The ${DATADOG_GRAPHS_ANNOTATION} annotation could not be read: ${parseError}. ` +
          'Expected a JSON array of { "title": "...", "query": "..." } objects.'
        }
      />
    );
  }

  const metricSeed = `savr.${entity.metadata.name
    .toLowerCase()
    .replace(/-/g, '_')}`;
  const editingGraph =
    dialog.editIndex !== undefined ? graphs[dialog.editIndex] : undefined;

  // Sections render as tabs; graphs without one land in "General". No tabs
  // row at all while nothing is sectioned.
  const sections = [
    ...new Set(graphs.map(graph => graph.section?.trim() || '')),
  ];
  const showTabs = sections.some(name => name !== '');
  const activeSection = sections.includes(selectedSection ?? '')
    ? selectedSection ?? ''
    : sections[0] ?? '';
  const visibleGraphs = graphs
    .map((graph, index) => ({ graph, index }))
    .filter(
      ({ graph }) =>
        !showTabs || (graph.section?.trim() || '') === activeSection,
    );

  return (
    <>
      <div className={classes.header}>
        <Typography variant="h5">Metrics</Typography>
        <Tooltip title={sourceHint[source]}>
          <InfoOutlinedIcon className={classes.infoIcon} />
        </Tooltip>
        <div className={classes.spacer} />
        {RANGES.map(range => (
          <button
            key={range.label}
            type="button"
            className={`${classes.rangePill} ${
              range.seconds === rangeSeconds ? classes.rangePillActive : ''
            }`}
            onClick={() => setRangeSeconds(range.seconds)}
          >
            {range.label}
          </button>
        ))}
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => {
              setReloadKey(k => k + 1);
              setHealthReload(k => k + 1);
            }}
          >
            <RetryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!readonly && source === 'saved' && (
          <Tooltip title="Reset to defaults">
            <IconButton size="small" onClick={handleReset}>
              <SettingsBackupRestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Open Datadog">
          <IconButton size="small" component={Link} to={DATADOG_UI_URL} noTrack>
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!readonly && (
          <Tooltip title="Manage health checks">
            <IconButton size="small" onClick={() => setHealthDialogOpen(true)}>
              <FavoriteBorderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!readonly && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ open: true })}
          >
            Add graph
          </Button>
        )}
      </div>
      <HealthStrip checks={health.checks} reloadKey={reloadKey} />
      {showTabs && (
        <Tabs
          value={activeSection}
          onChange={(_, value) => setSelectedSection(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.sectionTabs}
        >
          {sections.map(name => (
            <Tab key={name} value={name} label={name || 'General'} />
          ))}
        </Tabs>
      )}
      {graphs.length === 0 ? (
        <EmptyState
          missing="data"
          title="No graphs yet"
          description={
            `No savr.* custom metrics were found for ${entity.metadata.name}. ` +
            'Use "Add graph" to pick any Datadog metric - the selection is shared with everyone.'
          }
        />
      ) : (
        <Grid container spacing={2}>
          {visibleGraphs.map(({ graph, index }) => (
            <Grid item xs={12} md={6} key={`${graph.title}-${index}`}>
              <GraphCard
                graph={graph}
                rangeSeconds={rangeSeconds}
                reloadKey={reloadKey}
                onEdit={
                  readonly
                    ? undefined
                    : () => setDialog({ open: true, editIndex: index })
                }
                onDelete={
                  readonly
                    ? undefined
                    : () => persist(graphs.filter((_, i) => i !== index))
                }
              />
            </Grid>
          ))}
        </Grid>
      )}
      {dialog.open && (
        <GraphDialog
          open
          initial={editingGraph}
          metricSeed={metricSeed}
          sectionSeed={showTabs ? activeSection : undefined}
          sections={sections.filter(name => name !== '')}
          onClose={() => setDialog({ open: false })}
          onSave={graph => {
            setDialog({ open: false });
            persist(
              editingGraph
                ? graphs.map((g, i) => (i === dialog.editIndex ? graph : g))
                : [...graphs, graph],
            );
          }}
        />
      )}
      {healthDialogOpen && (
        <HealthDialog
          open
          initialChecks={health.checks}
          metricSeed={metricSeed}
          onClose={() => setHealthDialogOpen(false)}
          onSave={async checks => {
            setHealthDialogOpen(false);
            try {
              await saveHealth(apis, entity, checks);
              setHealthReload(k => k + 1);
            } catch (e) {
              alertApi.post({
                message: `Could not save health checks: ${e}`,
                severity: 'error',
              });
            }
          }}
        />
      )}
    </>
  );
}

// Components and Systems get the tab (a System aggregates metrics that span
// several services); with no configuration it falls back to auto-discovered
// savr.* metrics or an add-graph empty state.
const OBSERVABILITY_KINDS = ['component', 'system'];

const datadogEntityContent = EntityContentBlueprint.make({
  params: {
    path: 'observability',
    title: 'Observability',
    filter: entity => OBSERVABILITY_KINDS.includes(entity.kind.toLowerCase()),
    loader: async () => <DatadogContent />,
  },
});

// "Service health" card on the entity Overview page (right-hand info column):
// ping checks, metric health indicators and the latest CI run.
const serviceHealthCard = EntityCardBlueprint.make({
  name: 'service-health',
  params: {
    type: 'info',
    filter: entity => OBSERVABILITY_KINDS.includes(entity.kind.toLowerCase()),
    loader: async () => <ServiceHealthCard />,
  },
});

// Homepage widget: org-wide most viewed services this week, from the
// portal's own savr.backstage.page_views metrics.
const popularServicesWidget = HomePageWidgetBlueprint.make({
  name: 'popular-services',
  params: {
    name: 'HomePagePopularServices',
    title: 'Popular services',
    description: 'Most viewed catalog entities across SAVR this week',
    components: () =>
      import('./PopularServices').then(m => ({
        Content: m.PopularServicesContent,
      })),
  },
});

// Annotated explicitly: the homepage widget blueprint comes from
// plugin-home-react, which nests its own frontend-plugin-api copy - without
// the annotation, declaration emit fails with TS2742 (non-portable type).
export const datadogPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'datadog',
  extensions: [datadogEntityContent, serviceHealthCard, popularServicesWidget],
});
